import { z } from "zod";
import { StandardApiResponseSchema } from "../dbtDocs/types";
import { pageSchema } from "@/helpers/types";

export enum DatastoreTypeEnum {
  POSTGRES = "postgres",
  SNOWFLAKE = "snowflake",
  S3 = "s3",
  BIGQUERY = "bigquery",
  MYSQL = "mysql",
  TABLEAU = "tableau",
  DBT_CLOUD = "dbt_cloud",
  DBT_CORE = "dbt_core",
  AZURE_DATABRICKS = "azure_databricks",
  AZURE_DATABRICKS_PRIVATE_LINK = "azure_databricks_private_link",
  DATABRICKS = "databricks",
}

export type DatastoreType = DatastoreTypeEnum;

export enum DatastoreInstanceEditionEnum {
  ENTERPRISE = "ENTERPRISE",
  STANDARD = "STANDARD",
  BUSINESS_CRITICAL = "BUSINESS_CRITICAL",
  NA = "NA",
}

export type InstanceEdition = DatastoreInstanceEditionEnum;

// This can be used directly in zod schemas
export const datastoreTypeEnum = [
  DatastoreTypeEnum.POSTGRES,
  DatastoreTypeEnum.SNOWFLAKE,
  DatastoreTypeEnum.S3,
  DatastoreTypeEnum.BIGQUERY,
  DatastoreTypeEnum.MYSQL,
  DatastoreTypeEnum.TABLEAU,
  DatastoreTypeEnum.DBT_CLOUD,
  DatastoreTypeEnum.DBT_CORE,
  DatastoreTypeEnum.AZURE_DATABRICKS,
  DatastoreTypeEnum.AZURE_DATABRICKS_PRIVATE_LINK,
  DatastoreTypeEnum.DATABRICKS,
] as const;

// Zod schema for Datastore
export const DatastoreSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(datastoreTypeEnum),
  description: z.string().optional(),
  created_by: z.number().optional(),
  connection_info: z.string().optional(),
  last_modified_by: z.number().optional(),
  can_sync: z.boolean().optional(),
  auto_sync: z.boolean().optional(),
  last_synced_at: z.string().optional().nullable(),
  last_sync_status: z.string().optional().nullable(),
  instance_id: z.coerce.string().optional().nullable(),
  instance_edition: z.nativeEnum(DatastoreInstanceEditionEnum).optional().nullable(),
  is_organization_admin: z.boolean().optional().nullable(),
});

// Zod schema for DatastoreResponse
export const DatastoreResponseSchema = z.object({
  data_stores: z.array(DatastoreSchema),
});

// Infer types from schemas
export type Datastore = z.infer<typeof DatastoreSchema>;
export type DatastoreResponseInferred = z.infer<typeof DatastoreResponseSchema>;

export interface DatastoreTypeConfig {
  name: string;
  value: DatastoreType;
  formSteps: string[];
  enabled?: boolean;
}

export interface DatastoreFormData {
  name: string;
  type: DatastoreType;
  description: string;
  connection?: Record<string, unknown>;
  configuration?: Record<string, unknown>;
}

export interface DatastoreResponse {
  data_stores: Datastore[];
}

export const DatastoreTypeResponseSchema = z.array(
  z.object({
    value: z.enum(datastoreTypeEnum),
    enabled: z.boolean(),
    is_standalone: z.boolean(),
  })
);
export type DatastoreTypeResponse = z.infer<typeof DatastoreTypeResponseSchema>;

export enum SnowflakeConnectionType {
  PASSWORD = "password",
  KEY_PAIR = "key_pair",
}

export enum SnowflakePrivateKeyType {
  PEM = "pem",
  DER = "der",
}

export interface SnowflakeAuthKeyPair {
  private_key: string;
  passphrase: string;
  type: SnowflakePrivateKeyType;
}

export interface SnowflakeBasePayload {
  account: string;
  user: string;
  password: string;
  role?: string;
  warehouse?: string;
  name: string;
  type: string;
  description: string;
  key_pair: SnowflakeAuthKeyPair;
  auth_type: SnowflakeConnectionType;
}

export interface FetchSnowflakeDatabasesRequest extends SnowflakeBasePayload {
  database?: string;
}

export type FetchSnowflakeDatabasesResponse = Array<{
  comment?: string | null;
  created?: string | null;
  database_name?: string | null;
  database_owner?: string | null;
  is_transient?: string | null;
  last_altered?: string | null;
}>;

export const fetchSnowflakeDatabasesRequestSchema = z.object({
  account: z.string(),
  username: z.string(),
  password: z.string(),
  role: z.string().optional(),
  warehouse: z.string().optional(),
});

export const fetchSnowflakeDatabasesResponseSchema = z.array(
  z.object({
    comment: z.string().nullable().optional(),
    created: z.string().nullable().optional(),
    database_name: z.string().nullable().optional(),
    database_owner: z.string().nullable().optional(),
    is_transient: z.string().nullable().optional(),
    last_altered: z.string().nullable().optional(),
  })
);

// Test connection response schema
export const TestConnectionResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().optional(),
});
export type TestConnectionResponse = z.infer<typeof TestConnectionResponseSchema>;

// Sync datastore response schema
export const SyncDatastoreResponseSchema = StandardApiResponseSchema;
export type SyncDatastoreResponse = z.infer<typeof SyncDatastoreResponseSchema>;

// Toggle sync schedule response schema
export const ToggleSyncScheduleResponseSchema = z.object({
  ok: z.boolean(),
});
export type ToggleSyncScheduleResponse = z.infer<typeof ToggleSyncScheduleResponseSchema>;

// Base datastore request schema (common fields across all datastore types)
// Inferred from usage in DatastoreAdd components
const BaseDatastoreRequestSchema = z.object({
  name: z.string(),
  type: z.string(),
  description: z.string(),
});

// Postgres datastore request schema
// Inferred from PostgresConnection.tsx line 92-99
export const PostgresDatastoreRequestSchema = BaseDatastoreRequestSchema.extend({
  host: z.string(),
  port: z.string(),
  databases: z.array(z.string()),
  database: z.string().optional(),
  user: z.string(),
  password: z.string(),
});
export type PostgresDatastoreRequest = z.infer<typeof PostgresDatastoreRequestSchema>;

// Base Snowflake fields shared between both auth types
const SnowflakeBaseSchema = BaseDatastoreRequestSchema.extend({
  account: z.string(),
  user: z.string(),
  role: z.string().optional(),
  warehouse: z.string().optional(),
  databases: z.array(z.string()).optional(),
});

// Snowflake auth variants - defined once and reused
const SnowflakePasswordVariant = SnowflakeBaseSchema.extend({
  auth_type: z.literal(SnowflakeConnectionType.PASSWORD),
  password: z.string(),
});

const SnowflakeKeyPairVariant = SnowflakeBaseSchema.extend({
  auth_type: z.literal(SnowflakeConnectionType.KEY_PAIR),
  key_pair: z.object({
    private_key: z.string(),
    passphrase: z.string(),
    type: z.nativeEnum(SnowflakePrivateKeyType),
  }),
});

// Snowflake schema with discriminated union for auth_type
export const SnowflakeDatastoreRequestSchema = z.discriminatedUnion("auth_type", [
  SnowflakePasswordVariant,
  SnowflakeKeyPairVariant,
]);
export type SnowflakeDatastoreRequest = z.infer<typeof SnowflakeDatastoreRequestSchema>;

// Tableau datastore request schema
// Inferred from TableauConnection.tsx line 130-140
export const TableauDatastoreRequestSchema = BaseDatastoreRequestSchema.extend({
  url: z.string(),
  host: z.string(),
  site_name: z.string(),
  // PAT becomes optional for the "Add database" modal on the detail page
  // when the user keeps the existing PAT (toggle OFF) — backend then
  // looks the credentials up via ``datastore_id``.
  pat_name: z.string().optional(),
  pat_secret: z.string().optional(),
  user: z.string().optional(),
  password: z.string().optional(),
  databases: z.array(z.string()),
  worksheet_location: z.string().optional(),
  datastore_id: z.number().optional(),
  // Sibling site name (in the same instance as ``datastore_id``) whose PAT
  // should be reused. Lets the picker pass a name instead of a sibling row
  // ID — the backend resolves the sibling via instance_id + site_name.
  source_site_name: z.string().optional(),
  connection: z
    .object({
      projects: z.array(z.string()).optional(),
    })
    .optional(),
});
export type TableauDatastoreRequest = z.infer<typeof TableauDatastoreRequestSchema>;

// Union type for all datastore requests
// This covers all possible datastore creation payloads
export const AddDatastoreRequestSchema = z.union([
  PostgresDatastoreRequestSchema,
  SnowflakeDatastoreRequestSchema,
  TableauDatastoreRequestSchema,
]);
export type AddDatastoreRequest = z.infer<typeof AddDatastoreRequestSchema>;

// Request for adding databases to an existing datastore.
// The Tableau Cloud "Add database" modal extends the base payload with
// optional per-site PAT (toggle ON case) and a chosen worksheet_location.
export type AddDatabasesRequest = Datastore & {
  databases: string[];
  pat_name?: string;
  pat_secret?: string;
  user?: string;
  password?: string;
  site_name?: string;
  worksheet_location?: string;
  url?: string;
  host?: string;
  // Pinpoints the existing sibling data_store whose PAT should be reused
  // for this new site. Optional — backend falls back to instance-level
  // lookup when absent.
  datastore_id?: number;
  // Site name of the sibling whose PAT should be reused. Sent together with
  // ``datastore_id`` (page context) so the backend resolves the sibling via
  // instance_id + site_name without the frontend needing sibling row IDs.
  source_site_name?: string;
};

// Add datastore response schema
// Inferred from usage - API returns success response
export const AddDatastoreResponseSchema = z.object({
  ok: z.boolean().optional(),
  message: z.string().optional(),
});
export type AddDatastoreResponse = z.infer<typeof AddDatastoreResponseSchema>;

// Test datastore connection during creation request schema
// Inferred from usage in PostgresConnection, SnowflakeConnection, TableauConnection
// This is similar to AddDatastoreRequest but used for testing connection before creation

// Snowflake test connection schema - reuses variants with database field added
const SnowflakeTestConnectionSchema = z.discriminatedUnion("auth_type", [
  SnowflakePasswordVariant.extend({ database: z.string().optional() }),
  SnowflakeKeyPairVariant.extend({ database: z.string().optional() }),
]);

export const TestDatastoreConnectionDuringCreationRequestSchema = z.union([
  PostgresDatastoreRequestSchema.extend({
    database: z.string().optional(),
  }),
  SnowflakeTestConnectionSchema,
  TableauDatastoreRequestSchema,
]);
export type TestDatastoreConnectionDuringCreationRequest = z.infer<
  typeof TestDatastoreConnectionDuringCreationRequestSchema
>;

// Test datastore connection during creation response schema
// Inferred from usage in TableauConnection.tsx: response.ok is checked
// Tableau backends additionally return `site_role` and `can_publish_workbooks`
// so the Step 3 workbook UI can adapt for SiteAdministratorExplorer PATs.
export const TestDatastoreConnectionDuringCreationResponseSchema = z
  .object({
    ok: z.boolean(),
    message: z.string().optional(),
    site_role: z.string().optional(),
    can_publish_workbooks: z.boolean().optional(),
  })
  .passthrough(); // Allow additional unknown fields
export type TestDatastoreConnectionDuringCreationResponse = z.infer<
  typeof TestDatastoreConnectionDuringCreationResponseSchema
>;

// Available databases response schema
export const AvailableDatabasesResponseSchema = z.object({
  used_databases: z.array(z.string()),
  unused_databases: z.array(z.string()),
});
export type AvailableDatabasesResponse = z.infer<typeof AvailableDatabasesResponseSchema>;

// Datastore detail response schema (single datastore)
export const DatastoreDetailResponseSchema = DatastoreSchema;
export type DatastoreDetailResponse = z.infer<typeof DatastoreDetailResponseSchema>;

// Sync history item schema
export const SyncHistoryItemSchema = z.object({
  id: z.number(),
  event_id: z.number(),
  sync_status: z.string(),
  sync_event_type: z.string(),
  created_on: z.string(),
  // Tableau site this sync ran against; null for non-Tableau connections.
  site_name: z.string().nullish(),
});

export type SyncHistoryItem = z.infer<typeof SyncHistoryItemSchema>;

// Get sync history params
export interface GetSyncHistoryParams {
  page?: number;
  size?: number;
  start_time?: string;
  end_time?: string;
}

// Get sync history response schema
export const GetSyncHistoryResponseSchema = pageSchema(SyncHistoryItemSchema);

export type GetSyncHistoryResponse = z.infer<typeof GetSyncHistoryResponseSchema>;

// ============================================================================
// dbt Cloud Integration Types
// ============================================================================

export enum SyncType {
  SCHEDULED = "scheduled",
  REAL_TIME = "real_time",
}

// Response schemas for dbt Cloud API

export const DbtCloudScheduleResponseSchema = z.object({
  id: z.number(),
  sync_type: z.string(),
  cron_expr: z.string(),
  lookback_days: z.number(),
  is_active: z.boolean(),
  last_sync_at: z.string().nullable().optional(),
  last_sync_status: z.string().nullable().optional(),
  last_sync_error: z.string().nullable().optional(),
  created_at: z.string(),
  last_modified_at: z.string(),
});
export type DbtCloudScheduleResponse = z.infer<typeof DbtCloudScheduleResponseSchema>;

export const DbtCloudSyncDetailsSchema = z.object({
  synced_files: z.array(z.string()).optional().default([]),
  failed_files: z
    .array(z.object({ name: z.string(), error: z.string() }))
    .optional()
    .default([]),
  missing_artifacts: z.array(z.string()).optional().default([]),
  errors: z.array(z.string()).optional().default([]),
  ingestion_error: z.string().nullable().optional(),
});
export type DbtCloudSyncDetails = z.infer<typeof DbtCloudSyncDetailsSchema>;

export const DbtCloudProcessedRunResponseSchema = z.object({
  id: z.number(),
  dbt_cloud_run_id: z.string(),
  project_name: z.string().nullable().optional(),
  environment_name: z.string().nullable().optional(),
  run_finished_at: z.string(),
  processed_at: z.string(),
  artifacts_downloaded: z.array(z.string()),
  status: z.string(),
  error_message: z.string().nullable().optional(),
  sync_details: DbtCloudSyncDetailsSchema.nullable().optional(),
});
export type DbtCloudProcessedRunResponse = z.infer<typeof DbtCloudProcessedRunResponseSchema>;

export const DbtCloudEnvironmentResponseSchema = z.object({
  id: z.number(),
  external_environment_id: z.string(),
  environment_name: z.string().nullable().optional(),
  environment_type: z.string().nullable().optional(),
  dbt_core_integration_environment_id: z.number(),
  is_enabled: z.boolean(),
  last_processed_run_finished_at: z.string().nullable().optional(),
  created_at: z.string(),
  last_modified_at: z.string(),
  processed_runs_count: z.number().nullable().optional(),
});
export type DbtCloudEnvironmentResponse = z.infer<typeof DbtCloudEnvironmentResponseSchema>;

export const DbtCloudProjectResponseSchema = z.object({
  id: z.number(),
  external_project_id: z.string(),
  project_name: z.string().nullable().optional(),
  dbt_core_integration_id: z.number(),
  is_enabled: z.boolean(),
  created_at: z.string(),
  last_modified_at: z.string(),
  environments: z.array(DbtCloudEnvironmentResponseSchema).nullable().optional(),
});
export type DbtCloudProjectResponse = z.infer<typeof DbtCloudProjectResponseSchema>;

export const DbtCloudConnectionResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  account_id: z.string(),
  api_base_url: z.string(),
  is_active: z.boolean(),
  created_at: z.string(),
  last_modified_at: z.string(),
  created_by: z.number(),
  last_modified_by: z.number(),
  projects: z.array(DbtCloudProjectResponseSchema).nullable().optional(),
  schedule: DbtCloudScheduleResponseSchema.nullable().optional(),
});
export type DbtCloudConnectionResponse = z.infer<typeof DbtCloudConnectionResponseSchema>;

export const DbtCloudConnectionListResponseSchema = z.object({
  connections: z.array(DbtCloudConnectionResponseSchema),
});
export type DbtCloudConnectionListResponse = z.infer<typeof DbtCloudConnectionListResponseSchema>;

export const DbtCloudSyncResponseSchema = z.object({
  sync_triggered: z.boolean(),
  task_id: z.string().nullable().optional(),
  projects_queued: z.number(),
  environments_queued: z.number(),
  message: z.string().nullable().optional(),
});
export type DbtCloudSyncResponse = z.infer<typeof DbtCloudSyncResponseSchema>;

export const DbtCloudSyncHistoryResponseSchema = z.object({
  connection_id: z.number(),
  environments: z.array(DbtCloudEnvironmentResponseSchema),
  recent_runs: z.array(DbtCloudProcessedRunResponseSchema),
  // Pagination fields
  page: z.number(),
  pages: z.number(),
  size: z.number(),
  total: z.number(),
});
export type DbtCloudSyncHistoryResponse = z.infer<typeof DbtCloudSyncHistoryResponseSchema>;

export const DbtCloudDiscoveryResponseSchema = z.object({
  connection_id: z.number(),
  projects_discovered: z.number(),
  environments_discovered: z.number(),
  projects: z.array(DbtCloudProjectResponseSchema),
});
export type DbtCloudDiscoveryResponse = z.infer<typeof DbtCloudDiscoveryResponseSchema>;

// Discovered schemas (for validation response - not yet persisted to DB)
export const DbtCloudDiscoveredEnvironmentSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  environment_type: z.string().nullable().optional(),
});
export type DbtCloudDiscoveredEnvironment = z.infer<typeof DbtCloudDiscoveredEnvironmentSchema>;

export const DbtCloudDiscoveredProjectSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  environments: z.array(DbtCloudDiscoveredEnvironmentSchema).optional().default([]),
});
export type DbtCloudDiscoveredProject = z.infer<typeof DbtCloudDiscoveredProjectSchema>;

export const DbtCloudValidationResponseSchema = z.object({
  valid: z.boolean(),
  account_id: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  // Discovery data (only populated when valid=true)
  projects_discovered: z.number().nullish(),
  environments_discovered: z.number().nullish(),
  projects: z.array(DbtCloudDiscoveredProjectSchema).nullish(),
});
export type DbtCloudValidationResponse = z.infer<typeof DbtCloudValidationResponseSchema>;

// Conflict error schemas (returned when connection creation fails due to duplicate projects)
export const DbtCloudConflictingProjectSchema = z.object({
  project_name: z.string(),
  external_project_id: z.string(),
});
export type DbtCloudConflictingProject = z.infer<typeof DbtCloudConflictingProjectSchema>;

export const DbtCloudConflictErrorSchema = z.object({
  error_code: z.literal("CONFLICT_ERROR"),
  message: z.string(),
  conflicting_projects: z.array(DbtCloudConflictingProjectSchema),
});
export type DbtCloudConflictError = z.infer<typeof DbtCloudConflictErrorSchema>;

// Request schemas for dbt Cloud

export const DbtCloudConnectionCreateRequestSchema = z.object({
  name: z.string().min(1, "Name is required").max(128, "Name must be 128 characters or less"),
  api_token: z.string().min(1, "API token is required"),
  account_id: z
    .string()
    .min(1, "Account ID is required")
    .max(64, "Account ID must be 64 characters or less"),
  api_base_url: z.string().optional().default("https://cloud.getdbt.com/api/v2/"),
  sync_type: z.enum(["scheduled", "real_time"]).optional().default("scheduled"),
  schedule_cron: z.string().optional().default("0 0 * * *"),
  lookback_days: z.number().min(1).max(365).optional().default(30),
});
export type DbtCloudConnectionCreateRequest = z.infer<typeof DbtCloudConnectionCreateRequestSchema>;

export const DbtCloudProjectUpdateRequestSchema = z.object({
  is_enabled: z.boolean(),
});
export type DbtCloudProjectUpdateRequest = z.infer<typeof DbtCloudProjectUpdateRequestSchema>;

export const DbtCloudEnvironmentUpdateRequestSchema = z.object({
  is_enabled: z.boolean(),
});
export type DbtCloudEnvironmentUpdateRequest = z.infer<
  typeof DbtCloudEnvironmentUpdateRequestSchema
>;

export const DbtCloudScheduleUpdateRequestSchema = z.object({
  sync_type: z.enum(["scheduled", "real_time"]).optional(),
  cron_expr: z.string().optional(),
  lookback_days: z.number().min(1).max(365).optional(),
  is_active: z.boolean().optional(),
});
export type DbtCloudScheduleUpdateRequest = z.infer<typeof DbtCloudScheduleUpdateRequestSchema>;

export const DbtCloudConnectionUpdateRequestSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  api_token: z.string().min(1).optional(),
  api_base_url: z.string().optional(),
});
export type DbtCloudConnectionUpdateRequest = z.infer<typeof DbtCloudConnectionUpdateRequestSchema>;

export const DbtCloudValidateTokenRequestSchema = z.object({
  api_token: z.string().min(1, "API token is required"),
  account_id: z.string().min(1, "Account ID is required"),
  api_base_url: z.string().optional(),
});
export type DbtCloudValidateTokenRequest = z.infer<typeof DbtCloudValidateTokenRequestSchema>;

// Query keys for dbt Cloud
export const DBT_CLOUD_QUERY_KEYS = {
  CONNECTIONS: "dbt-cloud-connections",
  CONNECTION: "dbt-cloud-connection",
  SYNC_HISTORY: "dbt-cloud-sync-history",
  VALIDATE_TOKEN: "dbt-cloud-validate-token",
} as const;

// Query keys for dbt Core (migrated from integrations/constants.ts)
export const GET_INTEGRATIONS = "integrations";

// ============================================================================
// dbt Core Integration Types
// ============================================================================

export const DbtCoreIntegrationCreateRequestSchema = z.object({
  name: z
    .string({ message: "Integration name is required" })
    .min(3, { message: "Integration name must be at least 3 characters long" }),
  integration_type: z.enum(["dbt_core"], { message: "Integration type is required" }),
  environment: z
    .string({ message: "Environment name is required" })
    .min(3, { message: "Environment name must be at least 3 characters long" }),
});
export type DbtCoreIntegrationCreateRequest = z.infer<typeof DbtCoreIntegrationCreateRequestSchema>;

// ============================================================================
// Azure Databricks (makeshift, EFS-backed) Types
// ============================================================================

export const AzureDatabricksConnectionRequestSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().optional().default(""),
    azure_tenant_id: z.string().optional().default(""),
    databricks_account_id: z.string().min(1, "Databricks Account ID is required"),
    workspace_url: z
      .string()
      .min(1, "Workspace URL is required")
      .url("Workspace URL must be a valid URL"),
    sp_client_id: z.string().optional().default(""),
    sp_secret: z.string().optional().default(""),
    sql_warehouse_id: z.string().min(1, "SQL Warehouse ID is required"),
    system_catalog_scope: z.enum(["all", "specific"]),
    system_catalog_schemas: z.string().optional().default(""),
  })
  .refine(
    data =>
      data.azure_tenant_id.trim().length > 0 ||
      (data.sp_client_id.trim().length > 0 && data.sp_secret.trim().length > 0),
    {
      message: "Provide an Azure Tenant ID (Entra), or both Service Principal Client ID and Secret",
      path: ["azure_tenant_id"],
    }
  );
export type AzureDatabricksConnectionRequest = z.infer<
  typeof AzureDatabricksConnectionRequestSchema
>;

export const AzureDatabricksPrivateLinkConnectionRequestSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().default(""),
  workspace_resource_id: z.string().min(1, "Workspace Resource ID is required"),
  workspace_url: z.string().min(1, "Workspace URL is required"),
  storage_account_resource_id: z.string().min(1, "Storage Account Resource ID is required"),
  storage_account_fqdn: z.string().min(1, "Storage Account FQDN is required"),
  azure_region: z.string().min(1, "Azure Region is required"),
});
export type AzureDatabricksPrivateLinkConnectionRequest = z.infer<
  typeof AzureDatabricksPrivateLinkConnectionRequestSchema
>;

export const AzureDatabricksSavedConnectionResponseSchema = z.object({
  id: z.number(),
  config_type: z.string(),
  saved_at: z.string(),
});
export type AzureDatabricksSavedConnectionResponse = z.infer<
  typeof AzureDatabricksSavedConnectionResponseSchema
>;
